import React, { Component, Suspense, lazy } from 'react';
import { theme } from '../styles/theme';

// The admin panel ships as its own code file: it's downloaded when an admin
// opens it (or in the background once an admin signs in — see
// preloadAdminDashboard), so shoppers never download it.
const importAdminDashboard = () => import('./AdminDashboard');

const RELOADED_KEY = 'admin-panel-reloaded';

// A deploy replaces the code files, so a tab opened before it can ask for a
// file that no longer exists. Reload once to pick up the new site; if the
// panel still won't load, the error boundary below explains.
const loadAdminDashboard = () =>
  importAdminDashboard().then(
    (module) => {
      try {
        sessionStorage.removeItem(RELOADED_KEY);
      } catch {
        // Storage unavailable — nothing to clear.
      }
      return module;
    },
    (error) => {
      let alreadyReloaded = true;
      try {
        alreadyReloaded = sessionStorage.getItem(RELOADED_KEY) === '1';
        if (!alreadyReloaded) sessionStorage.setItem(RELOADED_KEY, '1');
      } catch {
        // Can't remember a reload without storage, so don't risk a loop.
      }
      if (alreadyReloaded) throw error;
      window.location.reload();
      return new Promise(() => {});
    },
  );

const AdminDashboard = lazy(loadAdminDashboard);

// Fetches the admin panel's code ahead of time. Failures are ignored here;
// opening the panel retries (and recovers) on its own.
export const preloadAdminDashboard = () => importAdminDashboard().catch(() => {});

const screenStyles = {
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.75rem',
  padding: '2rem',
  textAlign: 'center',
  backgroundColor: theme.colors.background,
};

class AdminPanelBoundary extends Component {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    console.error('Admin panel failed to load:', error);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div style={screenStyles} role="alert">
        <p style={{ margin: 0, color: theme.colors.text, fontSize: '1.125rem', fontWeight: 600 }}>
          The admin panel didn’t load.
        </p>
        <p style={{ margin: 0, color: theme.colors.textSecondary }}>
          Check your internet connection, then reload the page.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            marginTop: '0.5rem',
            padding: '0.75rem 1.5rem',
            border: 'none',
            borderRadius: theme.borderRadius.md,
            backgroundColor: theme.colors.primary,
            color: '#fff',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Reload
        </button>
      </div>
    );
  }
}

const LazyAdminDashboard = () => (
  <AdminPanelBoundary>
    <Suspense
      fallback={(
        <div style={screenStyles}>
          <p style={{ margin: 0, color: theme.colors.textSecondary }}>Loading the admin panel…</p>
        </div>
      )}
    >
      <AdminDashboard />
    </Suspense>
  </AdminPanelBoundary>
);

export default LazyAdminDashboard;
